/**
 * NMS Product Recommendations Component
 * Extends NMSBaseClass following NMS Component Instance Architecture
 * Provides AJAX product switching via Shopify Section Rendering API
 * Mirrors NMSColorSwatches pattern for recommendation sets
 */

class NMSProductRecommendations extends window.NMSBaseClass {
  #isInitialized = false;
  #eventListeners = [];
  #abortController = null;

  constructor(element) {
    super();
    this.element = element; // REQUIRED: Store element reference
    this.recommendationId = element.dataset.recommendationId || `recommendations-${Date.now()}`; // REQUIRED: Unique identifier

    // Store component data from Liquid
    this.sectionId = element.dataset.sectionId;
    this.productId = element.dataset.productId;
    this.mainSectionId = element.dataset.mainSectionId;
    this.sectionWrapperId = element.dataset.sectionWrapperId;

    // Store additional sections to update (comma-separated list of section types)
    this.additionalSections = element.dataset.additionalSections || '';

    // Control whether URL should be updated on swatch change
    // true on PDP, false on landing pages
    this.updateUrl = element.dataset.updateUrl !== 'false';

    // REQUIRED: Store in global registry for external access
    window.neemans.productRecommendations = window.neemans.productRecommendations || {};
    window.neemans.productRecommendations[this.recommendationId] = this;
  }

  init() {
    if (this.#isInitialized) return;

    // NOTE: super.init() is intentionally NOT called here.
    // NMSBaseClass.init() scans `document` globally for [data-nms-click] elements
    // which would double-bind our handlers since #bindEvents() already scopes to this.element.
    this.#setupRefs();
    this.#initializeState();
    this.#bindEvents();
    this.#listenForSectionUpdates();
    if (this.updateUrl) {
      this.#setupHistoryNavigation();
    }
    this.#isInitialized = true;
  }

  // REQUIRED: Scope all setup to this.element
  #setupRefs() {
    this.refs.swatches = this.element.querySelectorAll('[ref="swatches[]"]');
  }

  #initializeState() {
    this.setGlobalData(`productRecommendations.${this.recommendationId}`, {
      currentProduct: this.productId,
      isLoading: false,
      activeHandle: null,
    });
  }

  // Re-initialize after NMSColorSwatches replaces the section HTML
  #listenForSectionUpdates() {
    const handler = event => {
      const container = event.detail?.container;
      if (!container) return;
      const newElement = container.querySelector('[data-nms-product-recommendations]');
      if (newElement && newElement !== this.element) {
        this.element = newElement;
        this.#bindEvents();
      }
    };
    document.addEventListener('nms:color-content-updated', handler);
    this.#eventListeners.push({ element: document, event: 'nms:color-content-updated', handler });
  }

  #setupHistoryNavigation() {
    if (!history.state?.nmsProductSwitch) {
      history.replaceState(
        { nmsProductSwitch: true, url: window.location.href },
        document.title,
        window.location.href
      );
    }

    if (!window.__nmsPopstateHandlerAdded) {
      window.__nmsPopstateHandlerAdded = true;
      window.addEventListener('popstate', event => {
        if (event.state?.nmsProductSwitch) {
          window.location.reload();
        }
      });
    }
  }

  #bindEvents() {
    const clickElements = this.element.querySelectorAll('[data-nms-click]');

    for (const element of clickElements) {
      const handlerPath = element.getAttribute('data-nms-click');
      const [methodName, ...args] = handlerPath.split('/').filter(Boolean);

      if (this[methodName] && typeof this[methodName] === 'function') {
        const handler = event => {
          this[methodName].call(this, event, ...args);
        };

        element.addEventListener('click', handler, { capture: true });
        this.#eventListeners.push({ element, event: 'click', handler });
      } else {
        console.error('NMSProductRecommendations: Method not found:', methodName);
      }
    }
  }

  // Public API method - called via data-nms-click
  async handleRecommendationClick(event, productHandle) {
    event.preventDefault();
    event.stopPropagation();

    const swatchButton = event.currentTarget;

    // Don't do anything if clicking the already-active product
    if (swatchButton.classList.contains('nms-product-recommendations__swatch--active')) {
      return;
    }

    // Immediately block further clicks before any async work
    this.#showLoading();

    // Abort any in-flight request and start fresh
    if (this.#abortController) {
      this.#abortController.abort();
    }
    this.#abortController = new AbortController();

    try {
      const productUrl = swatchButton.dataset.productUrl;
      const productTitle = swatchButton.dataset.productTitle;

      this.setGlobalData(`productRecommendations.${this.recommendationId}.isLoading`, true);

      await this.#switchToProduct(productUrl, productHandle, productTitle, this.#abortController.signal);

      this.dispatchNMSEvent('product-recommendation:changed', {
        recommendationId: this.recommendationId,
        productHandle: productHandle,
        productUrl: productUrl,
        productTitle: productTitle,
        component: this,
      });
    } catch (error) {
      if (error.name === 'AbortError') return; // Superseded by a newer click — do nothing

      // Log the real error so we can diagnose instead of silently reloading
      console.error('NMSProductRecommendations: Switch failed:', error);
      this.handleError(error, 'Failed to switch product.');

      // Reset loading state so the user can try again
      this.#hideLoading();
    } finally {
      this.setGlobalData(`productRecommendations.${this.recommendationId}.isLoading`, false);
    }
  }

  async #switchToProduct(productUrl, productHandle, productTitle, signal) {
    try {
      // Build sections parameter - include main section and any additional sections
      const sectionsToFetch = [this.mainSectionId];

      if (this.additionalSections) {
        const additionalTypes = this.additionalSections.split(',').map(s => s.trim());

        additionalTypes.forEach(sectionType => {
          const sectionElement = document.querySelector(`[data-section-type="${sectionType}"]`);
          if (sectionElement && sectionElement.dataset.sectionId) {
            sectionsToFetch.push(sectionElement.dataset.sectionId);
          }
        });
      }

      const sectionsParam = sectionsToFetch.join(',');
      const response = await fetch(`${productUrl}?sections=${sectionsParam}`, { signal });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (!data[this.mainSectionId]) {
        throw new Error(`Invalid response: missing ${this.mainSectionId} section`);
      }

      await this.#updateProductSection(data[this.mainSectionId]);

      // Update additional sections if they were fetched
      sectionsToFetch.slice(1).forEach(sectionId => {
        if (data[sectionId]) {
          this.#updateAdditionalSection(sectionId, data[sectionId]);
        }
      });

      if (this.updateUrl) {
        this.#updateUrl(productUrl, productTitle);
      }

      this.#updateActiveState(productHandle);

      this.setGlobalData(`productRecommendations.${this.recommendationId}.currentProduct`, productHandle);
      this.setGlobalData(`productRecommendations.${this.recommendationId}.activeHandle`, productHandle);
    } finally {
      // Only restore UI if this request wasn't superseded by a newer click
      if (!signal.aborted) {
        this.#hideLoading();
      }
    }
  }

  async #updateProductSection(newHtml) {
    const sectionWrapper = document.querySelector(`#${this.sectionWrapperId}`);

    if (!sectionWrapper) {
      throw new Error('Main section wrapper not found');
    }

    const tempContainer = document.createElement('div');
    tempContainer.innerHTML = newHtml;
    const newSectionWrapper = tempContainer.querySelector(`#${this.sectionWrapperId}`);

    if (!newSectionWrapper) {
      throw new Error('New section wrapper not found in response');
    }

    sectionWrapper.innerHTML = newSectionWrapper.innerHTML;

    this.#reinitializeComponents(sectionWrapper);
  }

  #updateAdditionalSection(sectionId, newHtml) {
    const sectionWrapper = document.querySelector(`#shopify-section-${sectionId}`);

    if (!sectionWrapper) {
      console.warn(`NMSProductRecommendations: Additional section not found: shopify-section-${sectionId}`);
      return;
    }

    const tempContainer = document.createElement('div');
    tempContainer.innerHTML = newHtml;
    const newSectionWrapper = tempContainer.querySelector(`#shopify-section-${sectionId}`);

    if (!newSectionWrapper) {
      console.warn(`NMSProductRecommendations: New section wrapper not found in response for: ${sectionId}`);
      return;
    }

    sectionWrapper.innerHTML = newSectionWrapper.innerHTML;

    this.dispatchNMSEvent(
      'recommendation-additional-section-updated',
      {
        sectionId: sectionId,
        container: sectionWrapper,
        source: 'product-recommendations',
        recommendationId: this.recommendationId,
        component: this,
      },
      sectionWrapper
    );
  }

  #reinitializeComponents(container) {
    // Find new recommendations element in updated content and re-bind events
    const newRecommendations = container.querySelector('[data-nms-product-recommendations]');

    if (newRecommendations && newRecommendations !== this.element) {
      this.element = newRecommendations;
      this.#bindEvents();
    }

    // Fire NMS custom event for other components to re-initialize
    this.dispatchNMSEvent(
      'recommendation-content-updated',
      {
        container: container,
        source: 'product-recommendations',
        recommendationId: this.recommendationId,
        component: this,
      },
      container
    );
  }

  #updateUrl(newUrl, productTitle) {
    const newState = {
      nmsProductSwitch: true,
      product: productTitle,
      url: newUrl,
      timestamp: Date.now(),
      recommendationId: this.recommendationId,
    };

    history.pushState(newState, productTitle, newUrl);
    document.title = document.title.replace(/^[^|]*/, productTitle);

    this.dispatchNMSEvent('product-recommendation:url-updated', {
      recommendationId: this.recommendationId,
      newUrl: newUrl,
      productTitle: productTitle,
      component: this,
    });
  }

  #updateActiveState(activeHandle) {
    const allSwatches = this.element.querySelectorAll('.nms-product-recommendations__swatch');

    for (const swatch of allSwatches) {
      swatch.classList.remove('nms-product-recommendations__swatch--active');
    }

    const activeSwatch = this.element.querySelector(`[data-product-handle="${activeHandle}"]`);

    if (activeSwatch) {
      activeSwatch.classList.add('nms-product-recommendations__swatch--active');
    }

    this.dispatchNMSEvent('product-recommendation:active-changed', {
      recommendationId: this.recommendationId,
      activeHandle: activeHandle,
      component: this,
    });
  }

  #showLoading() {
    if (this.element) {
      this.element.style.opacity = '0.6';
      this.element.style.pointerEvents = 'none';
      this.element.setAttribute('aria-busy', 'true');
      this.element.classList.add('nms-loading');
    }
  }

  #hideLoading() {
    if (this.element) {
      this.element.style.opacity = '';
      this.element.style.pointerEvents = '';
      this.element.setAttribute('aria-busy', 'false');
      this.element.classList.remove('nms-loading');
    }
  }

  handleShowMore(event) {
    event.preventDefault();
    const list = event.currentTarget.closest('.nms-product-recommendations__list');
    if (!list) return;

    const hiddenSwatches = list.querySelectorAll('.nms-product-recommendations__swatch--overflow');
    for (const swatch of hiddenSwatches) {
      swatch.classList.remove('nms-product-recommendations__swatch--overflow');
    }

    event.currentTarget.remove();
  }

  // Public API
  getCurrentProductHandle() {
    return this.getGlobalData(`productRecommendations.${this.recommendationId}.activeHandle`);
  }

  isLoading() {
    return this.getGlobalData(`productRecommendations.${this.recommendationId}.isLoading`) || false;
  }

  destroy() {
    for (const listener of this.#eventListeners) {
      listener.element.removeEventListener(listener.event, listener.handler);
    }
    this.#eventListeners = [];

    if (window.neemans.productRecommendations?.[this.recommendationId]) {
      delete window.neemans.productRecommendations[this.recommendationId];
    }

    super.destroy();
    this.#isInitialized = false;
  }
}

// REQUIRED: Standard initialization pattern
function initNMSProductRecommendations() {
  if (!window.NMSBaseClass) {
    console.error('NMSProductRecommendations: NMSBaseClass required');
    return;
  }

  const elements = document.querySelectorAll('[data-nms-product-recommendations]');

  for (const element of elements) {
    if (!element.hasAttribute('data-initialized')) {
      const instance = new NMSProductRecommendations(element);
      instance.init();
      element.setAttribute('data-initialized', 'true');
    }
  }
}

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initNMSProductRecommendations);
} else {
  initNMSProductRecommendations();
}

// Handle theme editor reloads
document.addEventListener('shopify:section:load', event => {
  if (event.target.querySelector('[data-nms-product-recommendations]')) {
    initNMSProductRecommendations();
  }
});

// Make available in global namespace
window.neemans = window.neemans || {};
window.neemans.NMSProductRecommendations = NMSProductRecommendations;
window.initNMSProductRecommendations = initNMSProductRecommendations;
